import React, { useState, useEffect } from 'react';
import { Form, Select, DatePicker, Button, Space, Row, Col } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { SubscriptionFilters as SubscriptionFiltersType, SubscriptionStatus } from '../../types/subscription';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { SubscriptionType } from '../../types/subscription';
import { CompanyService } from '../../services/api/companyService';
import { Company } from '../../types/company';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface SubscriptionFiltersProps {
  filters: SubscriptionFiltersType;
  onChange: (filters: SubscriptionFiltersType) => void;
  onReset: () => void;
}

export const SubscriptionFilters: React.FC<SubscriptionFiltersProps> = ({
  filters,
  onChange,
  onReset,
}) => {
  const [form] = Form.useForm();
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSubscriptionTypes();
    loadCompanies();
  }, []);

  useEffect(() => {
    form.setFieldsValue(filters);
  }, [filters, form]);

  const loadSubscriptionTypes = async () => {
    try {
      const types = await SubscriptionService.getSubscriptionTypes();
      setSubscriptionTypes(types);
    } catch (error) {
      console.error('Error loading subscription types:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const companiesList = await CompanyService.getAll();
      setCompanies(companiesList);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleFilterChange = (changedValues: any, allValues: any) => {
    const newFilters: SubscriptionFiltersType = {
      ...allValues,
    };
    onChange(newFilters);
  };

  const handleReset = () => {
    form.resetFields();
    onReset();
  };

  const statusOptions: SubscriptionStatus[] = [
    'pending',
    'provisioned',
    'active',
    'onboarded',
    'suspended',
    'cancelled',
    'expired',
  ];

  return (
    <Form
      form={form}
      layout="inline"
      onValuesChange={handleFilterChange}
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[16, 16]} style={{ width: '100%' }}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="status" label="Status">
            <Select
              placeholder="All Statuses"
              allowClear
              style={{ width: '100%' }}
            >
              {statusOptions.map((status) => (
                <Option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item name="subscription_type_code" label="Type">
            <Select
              placeholder="All Types"
              allowClear
              loading={loading}
              style={{ width: '100%' }}
            >
              {subscriptionTypes.map((type) => (
                <Option key={type.code} value={type.code}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item name="company_id" label="Company">
            <Select
              placeholder="Filter by Company"
              allowClear
              showSearch
              style={{ width: '100%' }}
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={24}>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                htmlType="submit"
              >
                Apply Filters
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={handleReset}
              >
                Clear
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

